import React, { useState, useEffect, useCallback } from "react";
import { getUserSession } from "../component/userInfo";
import "./SempMain.css"; // CSS 적용
import SempList from "./SempList";
import SempForm from "./SempForm";
import PagingNav from "../component/PagingNav";
import SempDayMain from "./day/SempDayMain"; // Import the new component
import { Button, Alert, Row, Col, Modal } from "react-bootstrap";

const API_URL = "/json/semp_api.php";

const SempMain = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for SempPlan modal
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);

  // State for SempDay modal
  const [showDayManager, setShowDayManager] = useState(false);
  const [selectedPlanForDays, setSelectedPlanForDays] = useState(null);

  // Paging state
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [paging, setPaging] = useState(null);

  const fetchPlans = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}?action=list&page=${page}&perpage=${postsPerPage}`
        );
        const result = await response.json();
        if (result.success) {
          setPlans(result.data);
          setPaging(result.paging);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("데이터를 불러오는 중 오류가 발생했습니다.");
      }
      setLoading(false);
    },
    [postsPerPage]
  );

  useEffect(() => {
    fetchPlans(currentPage);
  }, [currentPage, fetchPlans]);

  const handleShowPlanModal = (plan = null) => {
    setCurrentPlan(plan);
    setShowPlanModal(true);
  };

  const handleHidePlanModal = () => {
    setShowPlanModal(false);
    setCurrentPlan(null);
  };

  const handleShowDayManager = (plan) => {
    setSelectedPlanForDays(plan);
    setShowDayManager(true);
  };

  const handleHideDayManager = () => {
    setShowDayManager(false);
    setSelectedPlanForDays(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSavePlan = async (planData) => {
    const userSession = getUserSession();
    if (!userSession) {
      setError("로그인이 필요합니다.");
      return;
    }

    const action = planData.semp_no ? "update" : "create";
    const creatorOrChanger = userSession.uid;

    const payload = {
      action,
      data: {
        ...planData,
        creator: planData.semp_no ? planData.creator : creatorOrChanger,
        changer: creatorOrChanger,
      },
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        alert(result.message);
        handleHidePlanModal();
        fetchPlans(action === "create" ? 1 : currentPage);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("저장 중 오류가 발생했습니다.");
    }
  };

  const handleDeletePlan = async (semp_no) => {
    if (window.confirm(`정말로 No.${semp_no} 계획을 삭제하시겠습니까?`)) {
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "delete", semp_no }),
        });
        const result = await response.json();
        if (result.success) {
          alert(result.message);
          fetchPlans(currentPage);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const handlePublishChange = async (semp_no, field, value) => {
    const userSession = getUserSession();
    if (!userSession) {
      setError("로그인이 필요합니다.");
      return;
    }

    const plan = plans.find(p => p.semp_no === semp_no);
    if (!plan) return;

    const publishStatus = value ? "Y" : "N";
    const fieldName = field === 'use_yn' ? '운영사이트' : '테스트 사이트';
    const actionText = publishStatus === 'Y' ? '발행' : '발행 중지';

    if (window.confirm(`[${plan.semp_title}] 계획을 ${fieldName} ${actionText} 하시겠습니까?`)) {
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "publish", semp_no, field, value: publishStatus, uid: userSession.uid }),
        });
        const result = await response.json();
        if (result.success) {
          alert(result.message);
          fetchPlans(currentPage);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("발행 상태 변경 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">특새 계획 관리</h2>
        <Button variant="primary" onClick={() => handleShowPlanModal()}>
          새 계획 추가
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <p>로딩 중...</p>
      ) : (
        <SempList
          plans={plans}
          onEdit={handleShowPlanModal}
          onDelete={handleDeletePlan}
          onManageDays={handleShowDayManager}
          onPublishChange={handlePublishChange}
        />
      )}

      {paging && (
        <Row className="mt-3">
          <Col className="d-flex justify-content-center">
            <PagingNav paging={paging} callback={handlePageChange} />
          </Col>
        </Row>
      )}

      {showPlanModal && (
        <SempForm
          show={showPlanModal}
          onHide={handleHidePlanModal}
          onSave={handleSavePlan}
          plan={currentPlan}
        />
      )}

      {selectedPlanForDays && (
        <Modal show={showDayManager} onHide={handleHideDayManager} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              일정 관리: {selectedPlanForDays.semp_title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SempDayMain parentPlan={selectedPlanForDays} />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default SempMain;
